import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { Helmet } from "react-helmet-async";
import { 
  CheckCircle, 
  Phone, 
  FileText,
  MapPin,
  Globe,
  Zap,
  Link as LinkIcon,
  BookOpen,
  AlertCircle,
  Search,
  Code,
  Calendar,
  ArrowRight,
  Image
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const LandscapingWebsiteSEO = () => {
  const faqs = [
    {
      question: "How long does it take to rank for 'patio installation near me'?",
      answer: "Typically 3–6 months for less competitive areas. Major metros may take 6–12+ months. High-ticket keywords like 'patio installation' or 'retaining wall contractors' take longer than general landscaping terms."
    },
    {
      question: "Should I create separate pages for patios, retaining walls, and plantings?",
      answer: "Yes. Each major service should have its own dedicated page with 400+ words, photos, pricing info, and FAQs. This helps you rank for service-specific searches."
    },
    {
      question: "Do I need city pages if I only serve one metro area?",
      answer: "Yes—create pages for the suburbs and neighborhoods within your metro. 'Patio installation in [Suburb]' searches are often less competitive than metro-wide searches."
    },
    {
      question: "How important is my project gallery for SEO?",
      answer: "Very important. Google can't 'see' photos, but it can read captions, alt text, and surrounding content. A well-organized gallery with descriptive captions helps you rank for image searches and builds trust."
    },
    {
      question: "Should I list prices on my landscaping website?",
      answer: "'Starting at' pricing or ranges work well. For example: 'Paver patios start at $15/sq ft.' This helps qualify leads and shows transparency without giving exact quotes."
    },
    {
      question: "How do I get more reviews for high-ticket projects?",
      answer: "Ask at the final walkthrough when the client is happiest. Send a follow-up text/email with a direct link to your Google review page. Make it easy and timely."
    },
    {
      question: "Does my website affect my Google Maps ranking?",
      answer: "Yes. Your website supports your Google Business Profile through NAP consistency, service alignment, and content relevance. A strong website helps your Maps listing rank higher."
    },
    {
      question: "What's the difference between SEO and Google Ads for landscaping?",
      answer: "SEO = organic rankings (free clicks, takes time, compounds over time). Google Ads = paid ads (instant visibility, pay per click, stops when you stop paying). Most landscapers benefit from both."
    }
  ];

  const commonMistakes = [
    { mistake: "Gallery pages with no captions or text", fix: "Add captions to every project: problem, solution, materials, result, city." },
    { mistake: "Huge uncompressed photos (5MB+ each)", fix: "Compress to WebP format, keep under 150kb, lazy load below-fold images." },
    { mistake: "All services crammed on one page", fix: "Create dedicated pages for patios, retaining walls, plantings, drainage, etc." },
    { mistake: "No city or area mentioned on pages", fix: "Include your service area in H1s, content, and meta descriptions." },
    { mistake: "No 'Get an Estimate' CTA above the fold", fix: "Phone number and estimate button should be visible without scrolling." },
    { mistake: "No reviews displayed for high-ticket work", fix: "Show project-specific reviews on service pages and gallery pages." },
    { mistake: "Phone number different from GBP listing", fix: "Use the exact same phone format everywhere: website, GBP, citations." },
    { mistake: "Service pages only have 100–150 words", fix: "Each service page needs 400–800+ words with process, materials, FAQs." }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/landscaping-website-seo/#faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Done-for-you websites for lawn care and landscaping companies that drive calls, quotes, and recurring clients."
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/landscaping-website-seo/#webpage",
    "name": "Landscaping Website SEO Guide | Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com/landscaping-website-seo/",
    "description": "Local SEO checklist for landscaping websites: service pages, city pages, galleries, reviews, schema, speed, and GBP alignment—step by step.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    }
  };

  const lastUpdated = "December 2024";

  return (
    <>
      <SEO 
        title="Landscaping Website SEO Guide"
        description="Local SEO checklist for landscaping websites: service pages, city pages, galleries, reviews, schema, speed, and GBP alignment—step by step."
        keywords="landscaping website seo, local seo for landscapers, hardscaping seo, landscaping service pages seo, patio installation seo"
        canonical="https://www.leafrushmarketing.com/landscaping-website-seo/"
        ogUrl="https://www.leafrushmarketing.com/landscaping-website-seo/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>

      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="container px-4 md:px-6 relative z-10">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <Badge variant="secondary" className="mb-4">
                Local SEO Guide
              </Badge>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Landscaping Website SEO: Local Ranking Checklist
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Get more estimate requests for patios, retaining walls, and design/build projects with a website optimized for local search.
              </p>
              
              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <a href="#get-started">
                  <Button size="lg" className="w-full sm:w-auto text-lg px-8">
                    <Phone className="mr-2 h-5 w-5" />
                    Get Started
                  </Button>
                </a>
                <NavLink to="/websites">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8">
                    See Website Subscription Pricing
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Answer Checklist */}
        <section className="pb-12">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <Card className="bg-primary/5 border-primary/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    Quick Answer: Top 10 Local SEO Actions for Landscapers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-muted-foreground">
                    {[
                      "Title tags include service + city (e.g., 'Patio Installation in Denver, CO')",
                      "Dedicated pages for patios, retaining walls, plantings, lighting, drainage",
                      "City/area pages for each primary service area",
                      "Project gallery organized by category with captions",
                      "Images compressed (WebP, <150kb) with descriptive alt text",
                      "Phone number matches your Google Business Profile exactly",
                      "Internal links: service pages → gallery → city pages → contact",
                      "CTA (call/estimate) visible above the fold",
                      "NAP (Name, Address, Phone) consistent everywhere",
                      "Schema markup on key pages (Organization, Service, FAQPage)"
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* How Local SEO Works */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-4">
                <Search className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  How Local SEO Works for Landscapers
                </h2>
              </div>
              <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
                Google ranks local businesses based on three factors. For landscapers, your website's depth and visual proof matter even more.
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">Relevance</h3>
                    <p className="text-sm text-muted-foreground">
                      Does your site clearly describe your services? Dedicated patio, retaining wall, and planting pages tell Google exactly what you do.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <MapPin className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">Proximity</h3>
                    <p className="text-sm text-muted-foreground">
                      How close is your business to the searcher? City pages and local content help Google understand your service area.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Globe className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">Prominence</h3>
                    <p className="text-sm text-muted-foreground">
                      How well-known is your business? Reviews, project galleries, and consistent NAP info across the web build prominence.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-8 p-4 bg-background border rounded-lg">
                <p className="text-sm text-muted-foreground text-center">
                  <strong className="text-foreground">Key insight for landscapers:</strong> High-ticket services like hardscaping need deeper pages with more photos, materials info, and project examples than basic lawn care. The more you invest in content, the better you'll rank.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* On-Page SEO Foundations */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-4xl font-bold text-center mb-4">
                On-Page SEO Foundations
              </h2>
              <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
                The essentials that every landscaping website needs to rank locally.
              </p>

              {/* Title Tags + H1s */}
              <div className="mb-10">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <span className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">1</span>
                      Title Tags + H1s
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground">
                      Your title tag and H1 should include your service type and city. This signals to Google exactly what you do and where.
                    </p>
                    
                    <div className="bg-accent/30 rounded-lg p-4 space-y-3">
                      <div>
                        <p className="font-medium text-sm text-primary">Title tag formula:</p>
                        <p className="text-muted-foreground font-mono text-sm">[Service] in [City], [State] | [Company Name]</p>
                      </div>
                      <div>
                        <p className="font-medium text-sm text-primary">Examples:</p>
                        <ul className="text-muted-foreground font-mono text-sm space-y-1">
                          <li>Patio Installation in Denver, CO | Rocky Mountain Landscapes</li>
                          <li>Retaining Wall Contractors in Austin, TX | Hill Country Hardscape</li>
                          <li>Landscape Design in Seattle, WA | Evergreen Outdoor Design</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Checklist:</h4>
                      <ul className="space-y-1 text-muted-foreground text-sm">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Title tag under 60 characters</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>H1 includes specific service + city</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>One H1 per page only</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Meta description under 160 characters with CTA</span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* URL Structure */}
              <div className="mb-10">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <span className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">2</span>
                      URL Structure
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground">
                      Clean, descriptive URLs help users and search engines understand your pages.
                    </p>
                    
                    <div className="bg-accent/30 rounded-lg p-4">
                      <p className="font-medium text-sm text-primary mb-2">Recommended URL patterns:</p>
                      <ul className="text-muted-foreground font-mono text-sm space-y-1">
                        <li>yoursite.com/patio-installation/</li>
                        <li>yoursite.com/retaining-walls/</li>
                        <li>yoursite.com/landscape-design/</li>
                        <li>yoursite.com/outdoor-lighting/</li>
                        <li>yoursite.com/service-areas/denver-co/</li>
                        <li>yoursite.com/gallery/patios/</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Checklist:</h4>
                      <ul className="space-y-1 text-muted-foreground text-sm">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Use hyphens, not underscores</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Include keywords in URL slug</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Keep URLs short and readable</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Avoid random numbers or query strings</span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Internal Linking */}
              <div className="mb-10">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <span className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">3</span>
                      Internal Linking
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground">
                      Internal links help Google crawl your site and guide visitors toward contacting you.
                    </p>
                    
                    <div className="bg-accent/30 rounded-lg p-4">
                      <p className="font-medium text-sm text-primary mb-2">Link flow for landscaping sites:</p>
                      <div className="flex flex-wrap items-center gap-2 text-sm mb-3">
                        <span className="bg-background px-3 py-1 rounded-full border">Home</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <span className="bg-background px-3 py-1 rounded-full border">Service Pages</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <span className="bg-background px-3 py-1 rounded-full border">Gallery</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <span className="bg-background px-3 py-1 rounded-full border">City Pages</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <span className="bg-background px-3 py-1 rounded-full border">Contact</span>
                      </div>
                      <p className="text-muted-foreground text-sm">
                        <strong className="text-foreground">Important:</strong> Gallery categories should link back to their service pages (patio gallery → patio service page).
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Checklist:</h4>
                      <ul className="space-y-1 text-muted-foreground text-sm">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Every page links to at least 2–3 other pages</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Service pages link to related gallery categories</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Use descriptive anchor text (not "click here")</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Every page links to contact/estimate form</span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Content Depth */}
              <div className="mb-10">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <span className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">4</span>
                      Content Depth
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground">
                      "Thin content" = pages with very little useful information. High-ticket services like hardscaping need substantial content.
                    </p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-destructive/10 rounded-lg p-4">
                        <p className="font-medium text-sm text-destructive mb-2">❌ Thin page (won't rank):</p>
                        <p className="text-muted-foreground text-sm">"We install patios. Contact us for a quote."</p>
                        <p className="text-xs text-muted-foreground mt-2">(~10 words, no detail)</p>
                      </div>
                      <div className="bg-primary/10 rounded-lg p-4">
                        <p className="font-medium text-sm text-primary mb-2">✅ Strong page (will rank):</p>
                        <p className="text-muted-foreground text-sm">Process, materials, design options, timeline, pricing range, FAQs, photos, CTAs.</p>
                        <p className="text-xs text-muted-foreground mt-2">(500–800+ words)</p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Minimum content targets:</h4>
                      <ul className="space-y-1 text-muted-foreground text-sm">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Hardscaping service pages: 600–1000+ words</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Softscape/planting pages: 400–600+ words</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>City pages: 300–500+ words with local context</span>
                        </li>
                      </ul>
                      <p className="text-sm text-muted-foreground mt-3">
                        Need help with content? See our <NavLink to="/landscaping-website-content" className="text-primary hover:underline">landscaping website content guide</NavLink>.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Service Pages That Rank */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-4">
                <FileText className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  Service Pages That Rank
                </h2>
              </div>
              <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
                Each major service needs its own page. Here's the structure that works for landscaping SEO.
              </p>

              <Card>
                <CardContent className="p-6">
                  <ul className="space-y-3 text-muted-foreground mb-6">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">H1:</strong> "[Service] in [City]" (e.g., "Patio Installation in Denver, CO")</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">What's included:</strong> Design, materials, installation, drainage, sealing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Materials/options:</strong> Pavers, natural stone, stamped concrete, flagstone</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Process + timeline:</strong> Consultation → design → install (typical 3–7 days)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Pricing range:</strong> "Starting at $X/sq ft" or "Projects from $X–$X"</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">CTA:</strong> Phone + "Get a Free Estimate" above fold and at bottom</span>
                    </li>
                  </ul>

                  <div className="text-center">
                    <NavLink to="/service-pages-that-rank">
                      <Button>
                        Read the Full Service Page Guide
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </NavLink>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* City/Service Area Pages */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-4">
                <MapPin className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  City/Service Area Pages
                </h2>
              </div>
              <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
                City pages help you rank for "landscaping in [city]" and "patio installation near [area]" searches.
              </p>

              <Card>
                <CardContent className="p-6 space-y-6">
                  <div>
                    <h4 className="font-semibold mb-3">How to create city pages without spam:</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span>Create individual pages for your top 5–10 cities/suburbs</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span>Add unique content: projects completed there, neighborhoods you've worked in</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span>Include photos from jobs in that city if possible</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span>Mention local factors: soil conditions, HOA requirements, climate</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-destructive/10 rounded-lg p-4">
                    <p className="font-medium text-sm text-destructive mb-2">⚠️ Avoid this:</p>
                    <p className="text-muted-foreground text-sm">
                      Creating 50 city pages that are identical except for the city name. Google sees this as doorway spam and it can hurt your rankings.
                    </p>
                  </div>

                  <div className="text-center">
                    <NavLink to="/service-area-pages-local-seo">
                      <Button>
                        Read the Full Service Area Guide
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </NavLink>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Gallery SEO */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-4">
                <Image className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  Gallery SEO (The Landscaper Differentiator)
                </h2>
              </div>
              <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
                Your project gallery is a major ranking asset—but only if it's optimized correctly.
              </p>

              <div className="space-y-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">How to Structure Your Gallery</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Organize projects by category so Google and visitors can find what they're looking for:
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <ul className="space-y-2 text-muted-foreground text-sm">
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-primary" />
                          Patios & Outdoor Living
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-primary" />
                          Retaining Walls
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-primary" />
                          Outdoor Lighting
                        </li>
                      </ul>
                      <ul className="space-y-2 text-muted-foreground text-sm">
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-primary" />
                          Plantings & Softscape
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-primary" />
                          Drainage Solutions
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-primary" />
                          Full Yard Transformations
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Caption Template</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-3">
                      Google can't "see" images—it reads the text around them. Use this caption formula:
                    </p>
                    <div className="bg-accent/30 rounded-lg p-4">
                      <p className="font-mono text-sm text-muted-foreground">
                        <span className="text-primary">[Problem]</span> → <span className="text-primary">[Solution]</span> → <span className="text-primary">[Materials]</span> → <span className="text-primary">[Result]</span> → <span className="text-primary">[City]</span>
                      </p>
                      <p className="mt-3 text-sm text-muted-foreground">
                        <strong className="text-foreground">Example:</strong> "This sloped backyard in Lakewood, CO was unusable. We installed a 600 sq ft Belgard paver patio with a 3-tier retaining wall, creating a level outdoor living space perfect for entertaining."
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Image SEO Rules</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">Format:</strong> Use WebP for smaller file sizes</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">File names:</strong> Descriptive (e.g., "paver-patio-installation-denver-co.webp")</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">Alt text:</strong> Natural descriptions with service + city when relevant</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">Size:</strong> Compress to under 150kb per image</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">Lazy loading:</strong> Enable for images below the fold</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Internal Links from Gallery</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Each gallery category should link to its corresponding service page:
                    </p>
                    <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
                      <li>• Patio gallery → /patio-installation/</li>
                      <li>• Retaining wall gallery → /retaining-walls/</li>
                      <li>• Lighting gallery → /outdoor-lighting/</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* GBP Alignment */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-4">
                <Globe className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  GBP Alignment (Website + Google Business Profile)
                </h2>
              </div>
              <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
                Your website and Google Business Profile must match perfectly. Inconsistencies hurt your local rankings.
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">NAP Consistency</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-3">
                      Name, Address, Phone must be identical on:
                    </p>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-primary" />
                        Your website footer
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-primary" />
                        Contact page
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-primary" />
                        Google Business Profile
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-primary" />
                        All citations (Yelp, Houzz, etc.)
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Services Alignment</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-3">
                      Services listed on GBP should match your website:
                    </p>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-primary" />
                        Same service names and descriptions
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-primary" />
                        Each GBP service links to a website page
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-primary" />
                        Primary category matches main offering
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Review Strategy for High-Ticket Projects</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-medium text-sm mb-2">When to ask:</p>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        <li>• At final walkthrough (client is happiest)</li>
                        <li>• Follow up via text with direct review link</li>
                        <li>• Include project type in your request</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-sm mb-2">Where to display:</p>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        <li>• Homepage (2–3 featured)</li>
                        <li>• Relevant service pages (patio review on patio page)</li>
                        <li>• Dedicated reviews page</li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <NavLink to="/growth-add-ons/gbp" className="text-primary hover:underline text-sm">
                      → Learn more about Google Business Profile optimization
                    </NavLink>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Technical SEO + Speed */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-4">
                <Zap className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  Technical SEO + Speed
                </h2>
              </div>
              <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
                Landscaping sites are image-heavy. Speed optimization is critical.
              </p>

              <div className="space-y-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Image-Heavy Site Optimization</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">Compress all images:</strong> Use WebP format, keep under 150kb each</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">Enable lazy loading:</strong> Only load images as visitors scroll to them</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">Limit giant sliders:</strong> Auto-playing carousels with 10+ images hurt speed</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">Use proper dimensions:</strong> Don't load 4000px images for 800px display</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Speed Checklist</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-x-6 gap-y-2">
                      {[
                        "Compress images to WebP format",
                        "Enable lazy loading for galleries",
                        "Minimize plugins and scripts",
                        "Use fast, reliable hosting",
                        "Enable browser caching",
                        "Minify CSS and JavaScript",
                        "Use a CDN for images",
                        "Optimize web fonts",
                        "Remove unused code",
                        "Test with PageSpeed Insights"
                      ].map((item, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Mobile UX Must-Haves</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">Sticky call button:</strong> Always visible for quick contact</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">Tap targets:</strong> Buttons at least 44x44 pixels</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">Form spacing:</strong> Fields easy to tap and fill on mobile</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">Gallery navigation:</strong> Easy swipe/tap through project photos</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Schema */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-4">
                <Code className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  Schema Markup
                </h2>
              </div>
              <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
                Schema is structured data that helps Google understand your business better.
              </p>

              <Card>
                <CardContent className="p-6 space-y-6">
                  <div>
                    <h4 className="font-semibold mb-3">Recommended schema types:</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">LocalBusiness or Organization:</strong> On homepage with NAP, hours, service area</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">Service:</strong> On each service page describing the offering</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">FAQPage:</strong> On pages with FAQ sections for potential rich snippets</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">WebPage:</strong> Basic page info with name, URL, description</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-accent/30 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">Important:</strong> Don't add fake reviews or ratings to schema. Google penalizes deceptive markup. Only include aggregateRating if you have a verified review collection system.
                    </p>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    All our <NavLink to="/landscaping-website-design" className="text-primary hover:underline">done-for-you landscaping websites</NavLink> include proper schema markup.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Common Mistakes */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
                Common Landscaping SEO Mistakes (And How to Fix Them)
              </h2>
              <p className="text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
                Avoid these landscaping website SEO mistakes that hurt your local rankings.
              </p>

              <div className="grid gap-4">
                {commonMistakes.map((item, index) => (
                  <Card key={index}>
                    <CardContent className="p-4 flex flex-col md:flex-row gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-semibold text-sm">Mistake:</p>
                          <p className="text-muted-foreground text-sm">{item.mistake}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 flex-1">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-semibold text-sm">Fix:</p>
                          <p className="text-muted-foreground text-sm">{item.fix}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 30-Day Action Plan */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-4">
                <Calendar className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  30-Day Local SEO Action Plan
                </h2>
              </div>
              <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
                A week-by-week plan to improve your landscaping website's local SEO.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">1</span>
                      Week 1: Foundation
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Fix title tags and H1s with service + city</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Add CTA above the fold on every page</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Verify NAP is consistent everywhere</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Set up contact form routing properly</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">2</span>
                      Week 2: Core Service Pages
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Create/improve patio installation page</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Create/improve retaining walls page</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Create/improve plantings/softscape page</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Add drainage, lighting pages if relevant</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">3</span>
                      Week 3: City Pages
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Create 5–10 city/area pages</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Add unique content per city</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Include local project photos if available</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Link city pages to services and contact</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">4</span>
                      Week 4: Gallery + GBP + Reviews
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Organize gallery by project category</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Add captions to all project photos</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Align GBP services with website</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Set up review request process</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Frequently Asked Questions
              </h2>
              
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-20 bg-primary/5">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Want Us to Handle Your Website SEO?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                We build landscaping websites with local SEO baked in—so you can focus on building outdoor spaces, not optimizing pages.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="#get-started">
                  <Button size="lg" className="w-full sm:w-auto text-lg px-8">
                    <Phone className="mr-2 h-5 w-5" />
                    Get Started
                  </Button>
                </a>
                <NavLink to="/contact">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8">
                    Contact Us
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* Calendly */}
        <CalendlyEmbed />

        {/* Related Reading */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-8">
                <BookOpen className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  Related Reading
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { title: "Landscaping Website Design", description: "See what's included in our done-for-you websites.", href: "/landscaping-website-design" },
                  { title: "Landscaping Website Cost", description: "Understand pricing options and what affects cost.", href: "/landscaping-website-cost" },
                  { title: "Service Pages That Rank", description: "Full guide to structuring service pages for SEO.", href: "/service-pages-that-rank" },
                  { title: "Service Area Pages for Local SEO", description: "How to create city pages that rank.", href: "/service-area-pages-local-seo" },
                  { title: "Landscaping Website Content Guide", description: "What to write on each page of your site.", href: "/landscaping-website-content" },
                  { title: "Website Subscription Pricing", description: "Our month-to-month website packages.", href: "/websites" },
                  { title: "Google Business Profile Optimization", description: "Get more calls from local search.", href: "/growth-add-ons/gbp" },
                  { title: "Lawn Care Website SEO Guide", description: "SEO guide for mowing & maintenance businesses.", href: "/lawn-care-website-seo" }
                ].map((item, index) => (
                  <NavLink key={index} to={item.href}>
                    <Card className="h-full hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <h3 className="font-semibold mb-1">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </CardContent>
                    </Card>
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Last Updated */}
        <section className="py-8 border-t">
          <div className="container px-4 md:px-6">
            <p className="text-center text-sm text-muted-foreground">
              Last updated: {lastUpdated}
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default LandscapingWebsiteSEO;
